import { LogFilterOverlay } from './log-filter.overlay';
import { meetsThreshold, tryParseLogLevel } from './logging.maps';
import { ConsoleDestination } from './console.destination';
import { LogLevel } from './logging.types';
import type {
    ChildLoggerOptions,
    Disposer,
    EmittableLogLevel,
    LogContextLayerInput,
    LogDestination,
    LogEntry,
    Logger,
    LoggerConfiguration,
    LoggerNodeDescriptor,
    LogMeta,
    MetaPredicates,
} from './logging.types';

const PRE_CONFIGURE_BUFFER_LIMIT = 100;

/** Shared surface for the root and all children: nine level methods plus cached child spawning. */
abstract class BaseLogger implements Logger {
    private readonly children = new Map<string, Logger>();

    constructor(
        readonly name: string,
        readonly chainMeta: LogMeta,
        readonly minLevel?: LogLevel
    ) {}

    protected abstract resolveEngine(): LoggerService;

    /** Spawn (or return the cached) named child; options apply on first creation only. */
    child(name: string, options: ChildLoggerOptions = {}): Logger {
        const existing = this.children.get(name);

        if (existing) {
            return existing;
        }

        const joinedName = this.name === '' ? name : `${this.name}.${name}`;
        const created = new ChildLogger(
            this.resolveEngine(),
            joinedName,
            { ...this.chainMeta, ...options.meta },
            options.minLevel ?? this.minLevel
        );
        this.children.set(name, created);

        return created;
    }

    trace(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Trace, this, message, meta);
    }

    debug(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Debug, this, message, meta);
    }

    info(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Info, this, message, meta);
    }

    notice(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Notice, this, message, meta);
    }

    warn(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Warn, this, message, meta);
    }

    error(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Error, this, message, meta);
    }

    critical(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Critical, this, message, meta);
    }

    alert(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Alert, this, message, meta);
    }

    emergency(message: string, meta?: LogMeta): void {
        this.resolveEngine().emit(LogLevel.Emergency, this, message, meta);
    }
}

/** A named descendant of the root logger. */
class ChildLogger extends BaseLogger {
    constructor(
        private readonly engine: LoggerService,
        name: string,
        chainMeta: LogMeta,
        minLevel?: LogLevel
    ) {
        super(name, chainMeta, minLevel);
    }

    protected resolveEngine(): LoggerService {
        return this.engine;
    }
}

/**
 * Root logger and engine: owns the level, destinations, ambient context
 * layers, the filter overlay, and a pre-configuration buffer replayed into
 * replay-accepting destinations on `configure()` so bootstrap output survives.
 */
export class LoggerService extends BaseLogger {
    private buffer: LogEntry[] = [];
    private configured = false;
    private readonly contextLayers = new Map<string, LogContextLayerInput>();
    private destinations: readonly LogDestination[] = [new ConsoleDestination()];
    private level: LogLevel = LogLevel.Debug;
    private readonly overlay = new LogFilterOverlay();

    constructor() {
        super('', {});
    }

    protected resolveEngine(): LoggerService {
        return this;
    }

    /** Apply environment configuration and flush the bootstrap buffer. */
    configure(configuration: LoggerConfiguration): void {
        this.level = configuration.level;
        this.destinations = [...configuration.destinations];

        if (configuration.overlayStorage) {
            this.overlay.attachStorage(configuration.overlayStorage);
        }

        const replayable = this.buffer.filter((entry) => meetsThreshold(entry.level, this.level));
        this.destinations
            .filter((destination) => destination.receivesReplay)
            .forEach((destination) =>
                replayable
                    .filter((entry) => meetsThreshold(entry.level, destination.minLevel ?? LogLevel.Trace))
                    .forEach((entry) => this.writeIsolated(destination, entry))
            );

        this.buffer = [];
        this.configured = true;
    }

    /** Register or replace a named ambient layer; returns a disposer that removes it if still current. */
    setContextLayer(layer: string, values: LogContextLayerInput): Disposer {
        this.contextLayers.set(layer, values);

        return () => {
            if (this.contextLayers.get(layer) === values) {
                this.contextLayers.delete(layer);
            }
        };
    }

    /** Override the runtime minimum level for console output (devtools API). */
    setMinimumLogLevel(level: string): void {
        const parsed = tryParseLogLevel(level);

        if (!parsed) {
            this.warn(`Ignoring unknown log level [${level}]`, { level });
            return;
        }

        this.overlay.setMinimumLevel(parsed);
    }

    /** Show console entries containing the substring, even below the minimum level (devtools API). */
    whiteListByMessage(message: string): void {
        this.overlay.addMessageRule('white', message);
    }

    /** Hide console entries containing the substring (devtools API). */
    blackListByMessage(message: string): void {
        this.overlay.addMessageRule('black', message);
    }

    /** Show console entries whose metadata matches, even below the minimum level (devtools API). */
    whiteListByMeta(predicates: MetaPredicates): void {
        this.overlay.addMetaRule('white', predicates);
    }

    /** Hide console entries whose metadata matches (devtools API). */
    blackListByMeta(predicates: MetaPredicates): void {
        this.overlay.addMetaRule('black', predicates);
    }

    /** Remove all runtime overlay settings and their persisted state (devtools API). */
    reset(): void {
        this.overlay.reset();
    }

    /** Dispatch one entry from any logger in the hierarchy. */
    emit(level: EmittableLogLevel, source: LoggerNodeDescriptor, message: string, meta?: LogMeta): void {
        const threshold = source.minLevel ?? this.level;

        if (!this.someDestinationMightAccept(level, threshold)) {
            return;
        }

        const entry: LogEntry = {
            context: this.resolveContext(),
            level,
            message,
            meta: { ...source.chainMeta, ...meta },
            name: source.name,
            timestamp: Date.now(),
        };

        if (!this.configured) {
            this.bufferEntry(entry);
        }

        this.destinations
            .filter((destination) => this.destinationAccepts(destination, entry, threshold))
            .forEach((destination) => this.writeIsolated(destination, entry));
    }

    private bufferEntry(entry: LogEntry): void {
        if (this.buffer.length >= PRE_CONFIGURE_BUFFER_LIMIT) {
            this.buffer.shift();
        }

        this.buffer.push(entry);
    }

    private destinationAccepts(destination: LogDestination, entry: LogEntry, threshold: LogLevel): boolean {
        if (!meetsThreshold(entry.level, destination.minLevel ?? LogLevel.Trace)) {
            return false;
        }

        return destination.respectsOverlay && this.overlay.isActive
            ? this.overlay.evaluate(entry, threshold)
            : meetsThreshold(entry.level, threshold);
    }

    private resolveContext(): LogMeta {
        const resolved: LogMeta = {};

        this.contextLayers.forEach((values) => {
            Object.assign(resolved, resolveLayer(values));
        });

        return resolved;
    }

    private someDestinationMightAccept(level: EmittableLogLevel, threshold: LogLevel): boolean {
        return this.destinations.some((destination) => {
            if (!meetsThreshold(level, destination.minLevel ?? LogLevel.Trace)) {
                return false;
            }

            return destination.respectsOverlay && this.overlay.isActive
                ? this.overlay.mightShow(level, threshold)
                : meetsThreshold(level, threshold);
        });
    }

    private writeIsolated(destination: LogDestination, entry: LogEntry): void {
        try {
            destination.write(entry);
        } catch (error) {
            // eslint-disable-next-line no-console -- last resort when a destination fails
            console.warn(`Log destination [${destination.name}] failed to write an entry`, error);
        }
    }
}

/** Resolve one context layer to its values, isolating resolver failures. */
function resolveLayer(values: LogContextLayerInput): LogMeta {
    if (typeof values !== 'function') {
        return values;
    }

    try {
        return values() ?? {};
    } catch {
        return {};
    }
}
